import React, { useRef, useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, MousePointerClick, AlertTriangle } from 'lucide-react';
import Overlay from './Overlay';
import Timeline from './Timeline';

const VideoPlayer = ({ videoUrl, analysisData, videoState, setVideoState }) => {
  // Defensive defaults: prevent crashes when props are undefined/null
  const safeVideoState = videoState || {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
  };
  const safeSetVideoState = setVideoState || (() => {});

  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const videoElementRef = useRef(null);

  const [isHovering, setIsHovering] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [waitingForClick, setWaitingForClick] = useState(false);

  // Detect if it's a YouTube URL
  const isYoutube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');

  // Track which events have already triggered an auto-pause
  const processedEvents = useRef(new Set());

  // Reset processed events when analysis data changes
  useEffect(() => {
    processedEvents.current.clear();
    setPlayerError(null);
    setWaitingForClick(false);
  }, [analysisData, videoUrl]);

  // Sync Video State (Native Video)
  useEffect(() => {
    if (!isYoutube && videoElementRef.current) {
        if (safeVideoState.isPlaying) {
            videoElementRef.current.play().catch(e => console.error("Playback failed", e));
        } else {
            videoElementRef.current.pause();
        }
    }
  }, [safeVideoState.isPlaying, isYoutube]);

  // Sync seek for native video
  useEffect(() => {
     if (!isYoutube && videoElementRef.current) {
         if (Math.abs(videoElementRef.current.currentTime - safeVideoState.currentTime) > 0.5) {
             videoElementRef.current.currentTime = safeVideoState.currentTime;
         }
     }
  }, [safeVideoState.currentTime, isYoutube]);


  const handleProgress = (state) => {
     if (isSeeking) return;

     const currentTime = state.playedSeconds;

     safeSetVideoState(prev => ({
         ...prev,
         currentTime: currentTime
     }));

     checkAutoPause(currentTime);
  };

  const handleNativeTimeUpdate = () => {
      if (isSeeking) return;
      if (videoElementRef.current) {
          const t = videoElementRef.current.currentTime;
          safeSetVideoState(prev => ({ ...prev, currentTime: t }));
          checkAutoPause(t);
      }
  };

  const checkAutoPause = (currentTime) => {
      if (analysisData && analysisData.events) {
          analysisData.events.forEach(event => {
              const timeDiff = currentTime - event.start_sec;

              if (timeDiff >= 0 && timeDiff < 0.25 && !processedEvents.current.has(event.id) && safeVideoState.isPlaying) {
                  safeSetVideoState(prev => ({ ...prev, isPlaying: false }));
                  setWaitingForClick(true);
                  processedEvents.current.add(event.id);
              }
          });
      }
  };

  const handleDuration = (duration) => {
      safeSetVideoState(prev => ({ ...prev, duration }));
  };

  const handleContainerClick = () => {
      if (waitingForClick) {
          setWaitingForClick(false);
          safeSetVideoState(prev => ({ ...prev, isPlaying: true }));
      } else {
          safeSetVideoState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
      }
  };

  const handleSeek = (time) => {
    setIsSeeking(true);
    setWaitingForClick(false);
    safeSetVideoState(prev => ({ ...prev, currentTime: time }));

    if (isYoutube && playerRef.current) {
        playerRef.current.seekTo(time, 'seconds');
    } else if (!isYoutube && videoElementRef.current) {
        videoElementRef.current.currentTime = time;
    }

    setTimeout(() => setIsSeeking(false), 200);
  };

  const toggleMute = (e) => {
      e.stopPropagation();
      const newVol = safeVideoState.volume === 0 ? 1 : 0;
      safeSetVideoState(prev => ({ ...prev, volume: newVol }));
  };

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen();
    } else {
        document.exitFullscreen();
    }
  };

  const handlePlayerError = (e) => {
      console.error("Video Player Error:", e);
      setPlayerError("Video playback restricted. Try a different video or upload a local file.");
  };

  return (
    <div
        ref={containerRef}
        className="relative w-full h-full bg-black group overflow-hidden shadow-2xl flex items-center justify-center select-none"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleContainerClick}
    >
      {/* Player Layer */}
      <div className="w-full h-full absolute inset-0 pointer-events-none">
          {isYoutube ? (
              <ReactPlayer
                key={videoUrl}
                ref={playerRef}
                url={videoUrl}
                width="100%"
                height="100%"
                playing={safeVideoState.isPlaying}
                volume={safeVideoState.volume}
                muted={safeVideoState.volume === 0}
                onProgress={handleProgress}
                onDuration={handleDuration}
                onError={handlePlayerError}
                progressInterval={50}
                controls={false}
                config={{
                    youtube: {
                        playerVars: {
                            showinfo: 0,
                            controls: 0,
                            modestbranding: 1,
                            rel: 0,
                            origin: typeof window !== 'undefined' ? window.location.origin : undefined
                        }
                    }
                }}
              />
          ) : (
              <video
                ref={videoElementRef}
                src={videoUrl}
                className="w-full h-full object-contain"
                onTimeUpdate={handleNativeTimeUpdate}
                onLoadedMetadata={(e) => handleDuration(e.currentTarget.duration)}
                playsInline
                muted={safeVideoState.volume === 0}
              />
          )}
      </div>

      {/* Error Overlay */}
      {playerError && (
          <div className="absolute inset-0 z-40 bg-black/90 flex flex-col items-center justify-center text-center p-6">
              <AlertTriangle className="text-red-500 mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-2">Playback Error</h3>
              <p className="text-zinc-400 max-w-md">{playerError}</p>
          </div>
      )}

      {/* Spatial Overlay Layer */}
      {!playerError && analysisData && (
        <Overlay
          events={analysisData.events || []}
          currentTime={safeVideoState.currentTime}
        />
      )}

      {/* Click To Continue Indicator */}
      {waitingForClick && !playerError && (
         <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30 flex items-center gap-2 px-6 py-3 bg-black/80 border border-amber-400/50 rounded-full animate-pulse backdrop-blur-md shadow-[0_0_20px_rgba(255,215,0,0.2)]">
             <MousePointerClick className="text-amber-400" size={20} />
             <span className="text-white font-bold tracking-widest text-lg">CLICK TO RESUME</span>
         </div>
      )}

      {/* Manual Pause Indicator */}
      {!safeVideoState.isPlaying && !waitingForClick && !playerError && (
        <div className="absolute top-4 right-4 z-20 px-3 py-1 bg-black/60 rounded text-xs font-mono text-zinc-400 border border-zinc-800">
            PAUSED
        </div>
      )}

      {/* Controls Bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-2 md:pb-4 pt-12 transition-opacity duration-300 z-30 ${isHovering || !safeVideoState.isPlaying ? 'opacity-100' : 'opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Scrubber */}
        <Timeline
            duration={safeVideoState.duration}
            currentTime={safeVideoState.currentTime}
            events={analysisData?.events || []}
            onSeek={handleSeek}
        />

        {/* Buttons Row */}
        <div className="flex items-center justify-between mt-1 md:mt-2">
          <div className="flex items-center gap-3 md:gap-4">
            <button
                onClick={(e) => { e.stopPropagation(); safeSetVideoState(prev => ({ ...prev, isPlaying: !prev.isPlaying })); }}
                className="text-white hover:text-amber-400 transition-colors"
            >
              {safeVideoState.isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>

            <button
                onClick={(e) => { e.stopPropagation(); handleSeek(0); }}
                className="text-zinc-400 hover:text-white transition-colors"
            >
                 <RotateCcw size={16} />
            </button>

            <div className="text-xs md:text-sm font-mono text-white">
              <span className="text-amber-400">{safeVideoState.currentTime.toFixed(1)}</span>
              <span className="text-zinc-500 mx-1">/</span>
              <span>{safeVideoState.duration.toFixed(1)}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4">
             <button
                onClick={toggleMute}
                className="text-zinc-400 hover:text-white transition-colors"
             >
                 {safeVideoState.volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
             </button>
             <button
                onClick={toggleFullscreen}
                className="text-zinc-400 hover:text-white transition-colors"
             >
                 <Maximize size={18} />
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
